import { Mask } from 'maska';
import React, { forwardRef, useEffect, useState, useRef, useLayoutEffect, } from 'react';
import styled, { css } from 'styled-components';
import chroma from 'chroma-js';
import { mergeRefs } from '../../helpers';
import { Icon } from '../Icon';
export const InputWrapper = styled.label `
  color: var(--input-color, ${({ theme }) => theme.style.inputDefaultTextColor});
  background-color: var(--input-background, ${({ theme }) => theme.style.inputDefaultBackgroundColor});
  display: block;
  position: relative;
  border-radius: ${({ theme }) => theme.style.inputBorderRadius};
  padding: ${({ theme }) => theme.style.inputPadding};
  font-weight: ${({ theme }) => theme.style.inputFontWeight};
  font-size: ${({ theme }) => theme.style.inputFontSize};
  cursor: text;
  box-sizing: border-box;
  transition: background-color 0.2s, box-shadow 0.2s, color 0.2s, opacity 0.2s;
  box-shadow: inset 0 0 0 var(--input-border, 1px)
      var(--input-border-color, ${({ theme }) => theme.style.inputDefaultBorderColor}),
    0 0 0 var(--input-outline, 0px) var(--input-outline-color, transparent),
    ${({ theme }) => theme.style.depressedShadow};

  --input-placeholder-color: ${({ theme }) => theme.style.inputDefaultPlaceholderTextColor};

  --input-background: ${({ theme, isHovered }) => isHovered && theme.style.inputDefaultHoveredBackgroundColor};
  --input-border-color: ${({ theme, isHovered }) => isHovered && theme.style.inputDefaultHoveredBorderColor};
  --input-placeholder-color: ${({ theme, isHovered, isFocussed }) => (isHovered || isFocussed) && theme.style.inputDefaultHoveredPlaceholderTextColor};

  --input-background: ${({ isFocussed, theme }) => isFocussed && theme.style.inputFocussedBackgroundColor};
  --input-border: ${({ isFocussed, theme }) => isFocussed && theme.style.inputFocussedBorder};
  --input-border-color: ${({ theme, isFocussed }) => isFocussed && theme.style.inputFocussedBorderColor};
  --input-outline: ${({ isFocussed, theme }) => isFocussed && theme.style.inputFocussedOutline};
  --input-outline-color: ${({ theme, isFocussed }) => isFocussed && theme.style.inputFocussedOutlineColor};

  cursor: ${({ isDisabled }) => isDisabled && 'not-allowed'};
  opacity: ${({ isDisabled }) => isDisabled && 0.75};

  --input-color: ${({ theme, hasError }) => hasError && theme.style.inputErrorTextColor};
  --input-border-color: ${({ theme, hasError }) => hasError && theme.style.inputErrorBorderColor};
  --input-background: ${({ hasError, theme }) => hasError && theme.style.inputErrorBackgroundColor};
  --input-outline-color: ${({ theme, hasError }) => hasError && theme.style.inputErrorOutlineColor};
  --input-placeholder-color: ${({ theme, hasError }) => hasError && theme.style.inputErrorPlaceholderTextColor};

  --input-line-height: ${({ theme }) => theme.style.inputLineHeight};

  ${({ size }) => size === 'small' &&
    css `
      border-radius: ${({ theme }) => theme.style.inputSmallBorderRadius};
      padding: ${({ theme }) => theme.style.inputSmallPadding};
      font-weight: ${({ theme }) => theme.style.inputSmallFontWeight};
      font-size: ${({ theme }) => theme.style.inputSmallFontSize};

      --input-line-height: ${({ theme }) => theme.style.inputSmallLineHeight};
    `}

  ${({ size }) => size === 'large' &&
    css `
      border-radius: ${({ theme }) => theme.style.inputLargeBorderRadius};
      padding: ${({ theme }) => theme.style.inputLargePadding};
      font-weight: ${({ theme }) => theme.style.inputLargeFontWeight};
      font-size: ${({ theme }) => theme.style.inputLargeFontSize};

      --input-line-height: ${({ theme }) => theme.style.inputLargeLineHeight};
    `}

  ${({ variant }) => variant === 'light' &&
    css `
      --input-shadow: ${({ theme, isFocussed }) => isFocussed && theme.primary.blendToBackground(1000, 100)};

      --input-background: ${({ theme, hasError }) => !hasError && theme.background};
      --input-border-color: ${({ theme, isFocussed, hasError }) => hasError && !isFocussed && theme.danger.blendToBackground(1000, 150)};

      --input-shadow: ${({ theme, hasError }) => hasError && theme.danger.blendToBackground(1000, 100)};
      --input-shadow-second: ${({ hasError, isFocussed }) => (hasError || isFocussed) && css `transparent`};
    `}

  ${({ variant }) => variant === 'line' &&
    css `
      background: none;
      box-shadow: none;
      padding-left: 0;
      padding-right: 0;
      &:before {
        content: '';
        height: 2px;
        position: absolute;
        left: -50%;
        right: -50%;
        bottom: 0;
        border-radius: 1px;
        transform: scale(0.5) translateZ(0);
        background-color: var(--input-line, ${({ theme }) => theme.secondary.blendToBackground(400)});
        box-shadow: 0 0 0 calc(var(--input-outline, 0px) * 2) var(--input-outline-color, transparent);
        transition: background-color 0.2s, box-shadow 0.2s;

        --input-border-color: ${({ theme, hasError }) => hasError && theme.danger.background};
        --input-line: ${({ hasError, isFocussed }) => (hasError || isFocussed) && css `var(--input-border-color)`};
      }
    `}
`;
export const InputLoadingWrapper = styled.div `
  position: absolute;
  inset: 1px;
  z-index: 1;
  display: flex;
  cursor: progress;
  justify-content: center;
  align-items: center;
  border-radius: inherit;
  background-color: ${({ theme }) => chroma(theme.style.inputDefaultBackgroundColor ?? theme.background)
    .alpha(0.025)
    .css()};
  backdrop-filter: blur(2px);
  transition: opacity 0.2s;

  ${({ $isLoading }) => !$isLoading &&
    css `
      opacity: 0;
      pointer-events: none;
    `}
`;
export const InputLoading = styled(Icon.Loading) `
  --icon-color: currentColor;

  transition: transform 0.25s ease 0.1s;

  ${({ $isLoading }) => !$isLoading &&
    css `
      transform: translateY(-8px) scale(0.75);
      transition-delay: 0s;
    `}
`;
export const InputContainer = styled.div `
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const InputElementSuffx = styled.div `
  margin-left: -8px;
`;
export const IconWrapper = styled.div `
  margin: -2px;
  transform: translateY(-0.5px);

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(600)};

    ${({ size }) => size === 'small' &&
    css `
        --icon-size: 16px;
        margin: 0 -4px 0 0;
      `}

    path {
      transition: stroke 0.2s, fill 0.2s;
    }

    ${({ isFocussed }) => isFocussed &&
    css `
        --icon-color: ${({ theme }) => theme.style.inputFocussedBorderColor};
      `}
  }
`;
export const InputElement = styled.input `
  appearance: none;
  background: none;
  border: none;
  outline: none;
  overflow: ${({ $isDynamicWidth }) => $isDynamicWidth && 'hidden'};
  padding: 0;
  margin: 0;
  color: inherit;
  width: 100%;
  max-width: 100%;
  letter-spacing: ${({ theme }) => theme.style.inputLetterSpacing};
  line-height: var(--input-line-height);
  height: var(--input-line-height);
  display: block;
  caret-color: ${({ theme }) => theme.primary.background};
  transition: opacity 0.2s;

  &:-webkit-autofill,
  &:-webkit-autofill:focus {
    transition: background-color 0s 9999s, color 0s 9999s;
  }

  [data-autocompleted] {
    background-color: transparent !important;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &::placeholder {
    color: var(--input-placeholder-color);
    transition: color 0.2s;
  }

  ${({ $isLoading }) => $isLoading &&
    css `
      opacity: 0;
      pointer-events: none;
    `}
`;
const Floating = styled.div `
  color: var(--input-placeholder-color);
  transition: color 0.2s, transform 0.2s, font-weight 0.2s;
  position: absolute;
  left: 0;
  top: 9px;
  line-height: var(--input-line-height);
  letter-spacing: ${({ theme }) => theme.style.inputLetterSpacing};
  transform: translateY(var(--input-floating-y, 1px)) scale(var(--input-floating-s, 1)) translateZ(0);
  transform-origin: 0% 50%;

  ${({ size }) => size === 'small' &&
    css `
      top: 5px;
    `}

  ${({ isFilled, isFocussed }) => (isFilled || isFocussed) &&
    css `
      --input-floating-y: -20px;
      --input-floating-s: 0.6;

      --input-floating-y: ${({ size }) => size === 'small' && css `-16px`};
      --input-floating-s: ${({ size }) => size === 'small' && css `.55`};
    `}
`;
export const Input = forwardRef((props, ref) => {
    const [isHoveredState, setIsHoveredState] = useState(props.isHovered ?? false);
    const [isFocussedState, setIsFocussedState] = useState(props.isFocussed ?? false);
    const [isFilledState, setIsFilledState] = useState(props.isFilled ?? false);
    const onChange = props.onChange ??
        (() => {
            /* NOOP */
        });
    const onFocus = props.onFocus ??
        (() => {
            /* NOOP */
        });
    const onBlur = props.onBlur ??
        (() => {
            /* NOOP */
        });
    const onKeyDown = props.onKeyDown ??
        (() => {
            /* NOOP */
        });
    const inputRef = useRef(null);
    const maskRef = useRef(new Mask({ mask: props.mask }));
    const styleProps = {
        isHovered: props.isHovered ?? isHoveredState,
        isFocussed: props.isFocussed ?? isFocussedState,
        isFilled: props.isFilled ?? isFilledState,
        isDisabled: props.isDisabled ?? false,
        isLoading: props.isLoading ?? false,
        isPressed: props.isPressed ?? false,
        hasError: props.hasError ?? false,
        variant: props.variant ?? 'default',
        size: props.size ?? 'default',
        isDynamicWidth: !!props.valueSuffix ?? false,
    };
    useLayoutEffect(() => {
        if (!inputRef.current || !props.valueSuffix) {
            return;
        }
        const spanSelected = inputRef.current.parentElement.parentElement.querySelector('& > .shadow-element');
        const span = (spanSelected || document.createElement('span'));
        if (!spanSelected) {
            span.classList.add('shadow-element');
            span.style.font = getComputedStyle(inputRef.current).font;
            span.style.letterSpacing = getComputedStyle(inputRef.current).letterSpacing;
            span.style.position = 'absolute';
            span.style.isolation = 'isolate';
            span.style.opacity = '0';
            span.style.pointerEvents = 'none';
            inputRef.current.parentElement.parentElement.appendChild(span);
        }
        span.textContent = (props.value || props.placeholder) ?? '';
        inputRef.current.style.width = `${span?.offsetWidth + 8}px`;
    }, [props.placeholder, props.value, props.valueSuffix, inputRef]);
    const handleClick = (e) => {
        e.stopPropagation();
        if (props.isDisabled) {
            return;
        }
        if (props.onClick) {
            props.onClick();
        }
        setIsFocussedState(true);
    };
    const handleHoverChange = (v) => {
        if (props.isDisabled || props.isLoading) {
            return;
        }
        if (props.onHoverChange) {
            props.onHoverChange(v);
        }
        setIsHoveredState(v);
    };
    const handleFocusChange = (v) => {
        if (props.isDisabled || props.isLoading) {
            return;
        }
        if (props.onFocusChange) {
            props.onFocusChange(v);
        }
        setIsFocussedState(v);
    };
    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && props.autoBlur) {
            inputRef.current?.blur();
        }
    };
    let pattern = props.pattern;
    if (props.type === 'email') {
        pattern = '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
    }
    useEffect(() => {
        if (props.isFocussed ?? isFocussedState) {
            inputRef.current?.focus();
        }
        else {
            inputRef.current?.blur();
        }
    }, [isFocussedState, props.isFocussed]);
    useEffect(() => {
        if (props.value) {
            setIsFilledState(props.value.length > 0);
        }
    }, [props.value, inputRef]);
    useEffect(() => {
        if (props.autoFocus) {
            setIsFocussedState(true);
        }
    }, [props.autoFocus]);
    const maskProps = !!props.mask
        ? {
            value: maskRef.current.masked(props.value ?? ''),
            onChange: (e) => onChange(maskRef.current.unmasked(e.target.value)),
            placeholder: maskRef.current.masked(props.placeholder ?? ''),
        }
        : {
            onChange: (e) => onChange(e.target.value),
            value: props.value ?? '',
            placeholder: props.placeholder,
        };
    return (React.createElement(InputWrapper, { style: props.style, className: props.className, onMouseEnter: () => handleHoverChange(true), onMouseMove: () => handleHoverChange(true), onMouseLeave: () => handleHoverChange(false), onClick: handleClick, ...styleProps },
        React.createElement(InputContainer, { ...styleProps },
            props.icon && React.createElement(IconWrapper, { ...styleProps }, props.icon),
            React.createElement(InputElement, { autoComplete: props.autoComplete, onKeyPress: handleKeyPress, autoFocus: props.autoFocus, disabled: props.isDisabled ?? false, type: props.type ?? 'text', pattern: pattern, "$isLoading": styleProps.isLoading, readOnly: props.readOnly ?? false, maxLength: props.maxLength, ...maskProps, onBlur: (e) => {
                    handleFocusChange(false);
                    onBlur(e);
                }, onFocus: (e) => {
                    handleFocusChange(true);
                    onFocus(e);
                }, onKeyDown: (e) => {
                    onKeyDown(e);
                }, "$isDynamicWidth": styleProps.isDynamicWidth, ref: mergeRefs([ref, inputRef]) }),
            props.valueSuffix && React.createElement(InputElementSuffx, null, props.valueSuffix)),
        props.children,
        props.variant === 'line' && React.createElement(Floating, { ...styleProps }, props.placeholder),
        React.createElement(InputLoadingWrapper, { "$isLoading": styleProps.isLoading },
            React.createElement(InputLoading, { "$isLoading": styleProps.isLoading }))));
});
//# sourceMappingURL=Input.js.map